import React from "react";
import { type NextPage } from "next";
import { SimpleGrid, Box, Flex } from "@chakra-ui/react";
import { Section, sectionSpacingY } from "components/section";
import { Page } from "components/page";
import { CenteredQuote } from "components/quotes";
import { getLandingPageProps, getPageMetaImage, type LandingPageProps } from "utils";
import { StackedLogos } from "components/logos";
import { Diptych } from "ui/diptych";
import Image from "next/image";
import { Button } from "ui/button";
import { TextColumn, TextTitle } from "ui/text";
import { MoneyIcon, PersonIcon, TargetIcon } from "ui/icons";
import { LegacyMediaHero } from "components/hero/legacy-media-hero";
import { integrationsCount } from "../../constants/integrations";
import heroImage from "../../../public/assets/solutions/sigma/hero.svg";
import fullWidthImage from "../../../public/assets/solutions/sigma/full-width.png";
import firstPairImage from "../../../public/assets/solutions/sigma/pair-1.png";
import secondPairImage from "../../../public/assets/solutions/sigma/pair-2.png";
import thirdPairImage from "../../../public/assets/solutions/sigma/pair-3.png";

const Sigma: NextPage<LandingPageProps> = ({ page, announcementBanner, defaultLogos, defaultMobileLogos }) => {
  return (
    <Page
      title={page.metaTitle}
      description={page.metaDescription}
      image={getPageMetaImage({ src: page.metaImageUrl, alt: page.metaTitle })}
      announcementBanner={announcementBanner}
      bgColor="new.blue.150"
      footerOptions={{ variant: "blue" }}
    >
      <LegacyMediaHero
        variant="blue"
        eyebrow={page.eyebrow}
        title={page.title}
        description={page.description}
        imageSrc={heroImage}
        imageAlt="Hightouch and Sigma logos."
      />

      <StackedLogos customers={defaultLogos} mobileCustomers={defaultMobileLogos} my="0" pt="small" />

      <Section>
        <Diptych
          direction="left"
          size="lg"
          mediaRatio={2 / 1}
          gap={{ base: "biggest", md: "huge" }}
          media={
            <Box>
              <Image
                src={fullWidthImage}
                alt="Data moving through a datawarehouse and Hightouch into various marketing tools and campaigns."
              />
            </Box>
          }
          titleAs="h2"
          title="You’ve invested in your dashboards — take action on them with Hightouch"
          actions={
            <Button hasRightChevron href="/blog/sigma-integration">
              Learn more
            </Button>
          }
        />
      </Section>

      <Section>
        <TextTitle size="lg" align="center" title="Bridge the gap between insights and action" maxW="2xl" />

        <SimpleGrid
          columns={{ base: 1, md: 3 }}
          gap={{ base: "bigger", md: "bigger" }}
          mt={{ base: "biggest", md: "huge" }}
          mx={{ base: "0", md: "auto" }}
        >
          <TextColumn
            iconChip={{ variant: "blue", icon: TargetIcon }}
            title="Sales & GTM teams"
            description="Sync sales territory planning to Salesforce."
          />

          <TextColumn
            iconChip={{ variant: "green", icon: MoneyIcon }}
            title="Finance Teams"
            description="Drive decisions with the most up-to-date forecast."
          />

          <TextColumn
            iconChip={{ variant: "yellow", icon: PersonIcon }}
            title="Marketing Teams"
            description="Personalize experiences with customer data points like LTV."
          />
        </SimpleGrid>
      </Section>

      <Section>
        <Flex flexDir="column" gap={sectionSpacingY}>
          <Diptych
            direction="right"
            mediaRatio={1294 / 737}
            media={
              <Box p="small">
                <Image src={firstPairImage} alt="" />
              </Box>
            }
            chip={{ text: "Single source of truth" }}
            titleAs="h2"
            title="Write once, use anywhere"
            description="With Hightouch, you can leverage your existing business logic in Sigma and ensure the tools used across your organization have access to the same data, unlocking the true value of your cloud data warehouse."
            actions={
              <Button hasRightChevron href="/blog/the-future-of-the-warehouse">
                Learn more
              </Button>
            }
          />

          <Diptych
            direction="left"
            mediaRatio={1405 / 1201}
            media={
              <Box p="small">
                <Image src={secondPairImage} alt="" />
              </Box>
            }
            chip={{ text: "Data activation" }}
            titleAs="h2"
            title="Take action on your insights"
            description="Activate the insights from your Sigma Workbooks in any of your sales, operations, finance, or marketing tools, so you can make decisions and take action on consistent, up-to-date data every time."
            actions={
              <Button hasRightChevron href="/integrations">
                See our {integrationsCount}+ integrations
              </Button>
            }
          />

          <Diptych
            direction="right"
            mediaRatio={1263 / 781}
            media={
              <Box p="small">
                <Image src={thirdPairImage} alt="" />
              </Box>
            }
            chip={{ text: "Accessible to business teams" }}
            titleAs="h2"
            title="Streamline your workflows"
            description="Select any element in a Sigma Workbook and seamlessly sync that data directly to the tools your business teams rely on every day to power their workflows. All without needing to know SQL."
            actions={
              <Button hasRightChevron href="/docs/extensions/sigma" target="_blank">
                Learn more
              </Button>
            }
          />
        </Flex>
      </Section>

      <Section my={0} pb={sectionSpacingY}>
        <CenteredQuote
          author={{ name: "Justin Thomas", job: "VP of Partnerships" }}
          company={{
            name: "Sigma",
            logo: "https://cdn.sanity.io/images/pwmfmi47/production/d1d5fca864fc04bbf5343e8b3242b9e85e24319d-612x177.png",
          }}
          quoteText="Sigma empowers data and business teams to maximize their data warehouse by providing fast, scalable, and secure exploratory analysis in a spreadsheet user interface. Together with Hightouch, we're building efficiency across the organization from data insights to business outcomes."
        />
      </Section>
    </Page>
  );
};

export default Sigma;

export const getStaticProps = getLandingPageProps("/solutions/sigma", {
  loadDefaultLogos: true,
  loadDefaultMobileLogos: true,
});
